import Ajv from 'ajv';
import ajvErrors from 'ajv-errors';
import addFormats from 'ajv-formats';
import { checkCondition } from './useConditionalFields.tsx';
import { TDynamicStoreField, useRegistrationStore } from '../store/store.ts';
import { FieldType, TDepositLimitsField, TField, TIdentificationNumberESField,
  TInputField, TStep } from '../types/types.ts';
import { flattenFields } from '../utils/utils.ts';

const isPasswordField = (field: TField): boolean => field.fieldType === FieldType.PasswordInput || 
         field.fieldType === FieldType.EsPasswordInput;

const useAreRequiredFieldsFilled = (formData: TDynamicStoreField | null, currentStepConfig: TStep) => {
  const { errors, customValidations, conditions, loading, metadata } = useRegistrationStore();

  const ajv = new Ajv({ allErrors: true });
  addFormats(ajv);
  ajvErrors(ajv);

  const isRequired = (field: TField): boolean => {
    if (isPasswordField(field)) {
      return true;
    }

    const schema = (field as TInputField).validation;
    if (!schema) {
      return false;
    }

    try {
      return !ajv.compile(schema)('');
    } catch {
      return true;
    }
  };

  if (!currentStepConfig) {
    return false;
  }

  const topLevelFields = currentStepConfig?.fields;
  const allFields = flattenFields(topLevelFields);

  const renderedFields = allFields.filter(field =>
    !(field as TInputField).dependsOn ||
    (field as TInputField)?.dependsOn?.every(dep =>
      checkCondition(dep, conditions, formData, metadata),
    ),
  );

  const noErrors = errors &&
    Object.keys(errors)
      .filter(key => renderedFields.some(field => field.name === key))
      .every(key => errors[key] === '');

  const noCustomValidations = customValidations &&
    Object.keys(customValidations)
      .filter(key =>
        renderedFields.some(field =>
          field.name === key ||
          (Array.isArray((field as TDepositLimitsField).selections) &&
            (field as TDepositLimitsField).selections.some(selection => selection.name === key)) ||
          (Array.isArray((field as TIdentificationNumberESField).selections) &&
            (field as TIdentificationNumberESField).selections.some(selection => selection.name === key)),
        ),
      )
      .every(key => customValidations[key] === '');

  const requiredFields = renderedFields.filter((field) => isRequired(field as TField));

  const allRequiredFieldsFilled = requiredFields.every((field) => (
    formData?.[field.name] === true
        || (
          typeof formData?.[field.name] === 'string'
          && formData?.[field.name]?.trim()) !== ''
  )
      && formData?.[field.name] !== undefined);

  return allRequiredFieldsFilled
    && noErrors
    && noCustomValidations
    && !loading;
};

export default useAreRequiredFieldsFilled;
